import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ComputedRef } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import PopupMenu from '@/components/dialogs/popupmenu/PopupMenu.vue';
import { PopupMenuItem } from '@/components/dialogs/popupmenu/PopupMenuItem';
import { AppName } from '@/data/Branding';
import BrowserDetection from '@/data/BrowserDetection';
import Integrations from '@/data/config/Integrations';
import { DoNotDisturbUser, DoNotDisturbUserRequest, FullUserDetails } from '@/data/datatypes/UserDetails';
import { getFriendlyDateTime } from '@/data/helpers/DateHelper';
import { setLogoutMarkerInLocalStorage } from '@/data/helpers/TokenHelper';
import IosNative from '@/data/IosNative';
import RouteNames from '@/router/RouteNames';
import { usePushNotificationStore } from '@/stores/PushNotification';
import { useUserStore } from '@/stores/User';

interface Emits {
  (e: 'userStatusClicked'): void,
  (e: 'toggleAppearOfflineClicked'): void,
  (e: 'showCustomDoNotDisturbDialog'): void,
  (e: 'close'): void,
}

export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileContextMenu',
  emits: ["userStatusClicked", "toggleAppearOfflineClicked", "showCustomDoNotDisturbDialog", "close"],
  setup(__props, { emit: __emit }) {

const userStore = useUserStore();
const pushNotificationStore = usePushNotificationStore();

const emit = __emit;

const currentUser: ComputedRef<FullUserDetails | null> = computed(() => {
  return userStore.currentUser;
});

const menuItems: ComputedRef<PopupMenuItem[]> = computed(() => {
  const items: PopupMenuItem[] = [
    new PopupMenuItem('item',
      statusText.value,
      {
        optionsType: 'callback',
        cssClass: 'midGreyIcon',
        textBefore: userStore.currentUserStatus ? statusEmoji.value : undefined,
        icon: userStore.currentUserStatus ? undefined : 'compact/emoji-compact',
        callback: () => emit('userStatusClicked'),
        elementTitle: 'Update your status',
      }
    ),
    new PopupMenuItem('item',
      toggleAppearOfflineMenuText.value,
      {
        optionsType: 'callback',
        cssClass: 'midGreyIcon',
        callback: () => emit('toggleAppearOfflineClicked'),
        elementTitle: toggleAppearOfflineMenuText.value,
      }
    ),
    new PopupMenuItem('menu', 'Do not disturb', {
      optionsType: 'base',
      cssClass: 'midGreyIcon',
      menuItems: doNotDisturbPopupMenuItems.value,
    }),
    new PopupMenuItem('divider', '', {
      optionsType: 'base',
      cssClass: 'between',
    }),
    new PopupMenuItem('item', 'Profile settings', {
      optionsType: 'router',
      route: profileSettingsRoute.value,
      elementTitle: 'Open profile settings',
    }),
    new PopupMenuItem('url', 'Privacy', {
      optionsType: 'url',
      url: 'https://cafex.to/privacy/',
      elementTitle: 'See privacy policy',
    }),
  ];
  if (showHelpIcon.value) {
    items.push(
      new PopupMenuItem('item', `${AppName} help`, {
        optionsType: 'base',
        elementId: 'triggerHelp-profile',
        elementTitle: `Get help with ${AppName}`,
      })
    );
  }
  items.push(
    new PopupMenuItem('divider', '', {
      optionsType: 'base',
      cssClass: 'between',
    })
  );
  items.push(
    new PopupMenuItem('item', 'Logout', {
      optionsType: 'callback',
      callback: logoutClicked,
      elementTitle: 'Log out of the system',
    })
  );
  return items;
});

const profileSettingsRoute: ComputedRef<RouteLocationRaw> = computed(() => {
  return { name: RouteNames.PROFILE_SETTINGS_ROUTE_NAME };
});

const showHelpIcon: ComputedRef<boolean> = computed(() => {
  return !!((window as unknown) as { pendo: unknown }).pendo;
});

const statusEmoji: ComputedRef<string> = computed(() => {
  return userStore.currentUserStatus ? userStore.currentUserStatus.emoji : '💬';
});

const statusText: ComputedRef<string> = computed(() => {
  return userStore.currentUserStatus ? userStore.currentUserStatus.text : 'Update your status';
});

const appearOffline: ComputedRef<boolean> = computed(() => {
  return currentUser.value?.appearOffline ?? false;
});

const toggleAppearOfflineMenuText: ComputedRef<string> = computed(() => {
  return `Set yourself as ${appearOffline.value ? 'active' : 'away'}`;
});

const doNotDisturbActiveUntil: ComputedRef<string> = computed(() => {
  if (!currentUser.value?.doNotDisturbActive) {
    return 'Do not disturb is off';
  }

  let toReturn = 'On';
  if (currentUser.value.doNotDisturbActiveUntil) {
    const endDate = new Date(currentUser.value.doNotDisturbActiveUntil);
    toReturn = toReturn + ' until ' + getFriendlyDateTime(endDate);
  }
  return toReturn;
});

const doNotDisturbPopupMenuItems: ComputedRef<PopupMenuItem[]> = computed(() => {
  const enableDoNotDisturbItems : PopupMenuItem[] = [
    new PopupMenuItem('item', 'Do not disturb for 30 minutes',
      { optionsType: 'callback', callback: () => doNotDisturb30MinsClicked() }),
    new PopupMenuItem('item', 'Do not disturb for 1 hour',
      { optionsType: 'callback', callback: () => doNotDisturb1HourClicked() }),
    new PopupMenuItem('item', 'Do not disturb until tomorrow',
      { optionsType: 'callback', callback: () => doNotDisturbTomorrowClicked() }),
    new PopupMenuItem('item', 'Do not disturb until custom',
      { optionsType: 'callback', callback: () => doNotDisturbCustomClicked() }),
  ];

  if (currentUser.value?.doNotDisturbActive) {
    return [
      new PopupMenuItem('text', doNotDisturbActiveUntil.value),
      new PopupMenuItem('divider', '', {
        optionsType: 'base',
        cssClass: 'between',
      }),
      new PopupMenuItem('item', 'Turn off do not disturb',
        { optionsType: 'callback', callback: () => turnOffDoNotDisturbClicked() }),
      new PopupMenuItem('menu', 'Adjust', {
        optionsType: 'base',
        menuItems: enableDoNotDisturbItems,
      })
    ];
  }
  return [
    new PopupMenuItem('text', doNotDisturbActiveUntil.value),
    new PopupMenuItem('divider', '', {
      optionsType: 'base',
      cssClass: 'between',
    }),
    ...enableDoNotDisturbItems
  ];
});

function doNotDisturb30MinsClicked(): void {
  const doNotDisturb: DoNotDisturbUser = {
    schedule: 'THIRTY_MINS'
  };
  updateDoNotDisturb(doNotDisturb);
}

function doNotDisturb1HourClicked(): void {
  const doNotDisturb: DoNotDisturbUser = {
    schedule: 'ONE_HOUR'
  };
  updateDoNotDisturb(doNotDisturb);
}

function doNotDisturbTomorrowClicked(): void {
  // Get the nearest midnight in the future in the user's current timezone
  const timeTomorrowMidnight = new Date().setHours(24, 0, 0, 0);
  const doNotDisturb: DoNotDisturbUser = {
    schedule: 'CUSTOM',
    custom: timeTomorrowMidnight,
  };
  updateDoNotDisturb(doNotDisturb);
}

function doNotDisturbCustomClicked(): void {
  emit('showCustomDoNotDisturbDialog');
}

function turnOffDoNotDisturbClicked(): void {
  const doNotDisturb: DoNotDisturbUser = {
    schedule: 'NONE'
  };
  updateDoNotDisturb(doNotDisturb);
}

function updateDoNotDisturb(doNotDisturb: DoNotDisturbUser): void {
  if (currentUser.value) {
    const doNotDisturbRequest: DoNotDisturbUserRequest = {
      id: currentUser.value.id,
      doNotDisturbUser: doNotDisturb
    };
    userStore.updateCurrentUserDoNotDisturb(doNotDisturbRequest);
  }
}

async function logoutClicked(): Promise<void> {
  try {
    await pushNotificationStore.unregisterPushNotifications();
  } catch (error) {
    // Probably nothing we can do about it
  }
  await userStore.clearUserState();

  // Indicates in the local storage that we are doing a logout. Other Fender tabs are monitoring it
  // and they will clean up states on their side too. This ensures a more consistent behaviour and
  // stops other tabs from automatically re-authenticate via silent refresh.
  setLogoutMarkerInLocalStorage();

  window.location.replace(`${Integrations.BE}/logout`);
  if (BrowserDetection.isIosApp()) {
    IosNative.invokeNative('startNativeLogin', { debugInfo: 'ProfileContextMenu::logoutClicked' });
  }

  emit('close');
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(PopupMenu, {
    items: menuItems.value,
    horizontalPosition: "left"
  }, {
    menuOpener: _withCtx(() => [
      _renderSlot(_ctx.$slots, "default")
    ]),
    _: 3
  }, 8, ["items"]))
}
}

})